import { Nav, NavEntryProps, SiteContainer } from "@dash/nav";
import { useTheme } from "@emotion/react";
import { API_URL, CRM_URL, DOCS_URL, INVOICES_URL, POINTS_URL, QR_SYNC_URL, useConfig } from "@megarax/dev-config";
import { AppLinkTree } from "@megarax/links";
import { useContext, useEffect, useState } from "react";

import { IamAuthContext } from "@megaron/auth-react";
import { HttpUri } from "@megaron/http-uri";
import { getMegaraxPath, MegaraxUri } from "@megaron/megarax-uri";

import { MegaraxNavFooter } from "./MegaraxNavFooter";
import { WelcomeScreen } from "./WelcomeScreen";

const environmentTemplate = process.env["NX_PUBLIC_ENVIRONMENT_TEMPLATE"];

type Props = {
  children?: React.ReactNode;
  navEntries: NavEntryProps[];
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  logo?: React.ReactNode;
  backgroundImageSrc?: string;
  className?: string;
  bypassAuth?: boolean;
  hideHeader?: boolean;
  app?: string;
};

export const MegaraxWebappContainer: React.FunctionComponent<Props> = (props) => {
  const authCtx = useContext(IamAuthContext);

  const theme = useTheme();

  const { config } = useConfig();
  const { apiUrl, crmUrl, docsUrl, invoicesUrl, pointsUrl, qrSyncUrl } = config;

  const [isProductionUsed, setIsProductionUsed] = useState<boolean>(false);

  useEffect(() => {
    if (
      apiUrl === API_URL ||
      crmUrl === CRM_URL ||
      docsUrl === DOCS_URL ||
      invoicesUrl === INVOICES_URL ||
      pointsUrl === POINTS_URL ||
      qrSyncUrl === QR_SYNC_URL
    ) {
      setIsProductionUsed(true);
      return;
    }

    setIsProductionUsed(false);
  }, [apiUrl, crmUrl, docsUrl, invoicesUrl, pointsUrl, qrSyncUrl]);

  useEffect(() => {
    updateThemeColor(theme.colors.primary);
  }, [theme.colors.primary]);

  return (
    <SiteContainer
      className={props.className}
      welcomeScreen={
        authCtx.iamUser || props.bypassAuth ? undefined : (
          <WelcomeScreen logo={props.logo} title={props.title} subtitle={props.subtitle} />
        )
      }
      backgroundImageSrc={props.backgroundImageSrc}
      nav={
        <Nav
          app={props.app}
          title={props.title}
          subtitle={props.subtitle}
          logo={props.logo}
          backgroundImageSrc={props.backgroundImageSrc}
          footer={<MegaraxNavFooter />}
          entries={props.navEntries}
          hideHeader={props.hideHeader}
          showProductionEnvAlert={isProductionUsed && !environmentTemplate}
        />
      }
    >
      {props.children}
    </SiteContainer>
  );
};

export const appLinkTreeToNavEntries = (appLinkTree: AppLinkTree | undefined): NavEntryProps[] =>
  appLinkTree?.pageLinks.map((link) => {
    const href = getMegaraxPath.try(link.uri) ?? link.uri;
    const isExternal = !MegaraxUri.isUri(link.uri);

    return {
      to: href,
      icon: <link.Icon />,
      children: link.name || "",
      isActive: link.isActive,
      isExternal,
    };
  }) ?? [];

const updateThemeColor = (color: string) => {
  const metaThemeColor = document.querySelector("meta[name=theme-color]");
  if (metaThemeColor) {
    metaThemeColor.setAttribute("content", color);
  } else {
    const meta = document.createElement("meta");
    meta.setAttribute("name", "theme-color");
    meta.setAttribute("content", color);
    document.head.appendChild(meta);
  }
};
